/* Shared storefront performance guardrails.
 * Keeps review placeholders stable, prevents stale Judge.me responses from
 * winning a race, and avoids expensive observer work during unrelated DOM
 * mutations. This file is intentionally dependency-free and loaded once.
 */
(() => {
  if (window.__rhpPerformanceReady) return;
  window.__rhpPerformanceReady = true;

  const reviewSelector = '[data-rhp-review-root]';
  const reviewRequests = new Map();
  const nativeFetch = window.fetch.bind(window);

  // Abort superseded filtered review requests. Summary/default requests are
  // left independent so the first usable dataset can settle immediately.
  window.fetch = (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    if (!url.includes('api.judge.me/reviews/reviews_for_widget')) {
      return nativeFetch(input, init);
    }
    const parsed = new URL(url, window.location.href);
    const productId = parsed.searchParams.get('product_id') || '';
    const query = parsed.searchParams.get('search') || '';
    const rating = parsed.searchParams.get('filter_rating') || '';
    const filtered = Boolean(query || rating);
    if (!filtered || !productId) return nativeFetch(input, init);
    const previous = reviewRequests.get(productId);
    previous?.abort();
    const controller = new AbortController();
    reviewRequests.set(productId, controller);
    const requestInit = { ...init, signal: controller.signal };
    const request = nativeFetch(input, requestInit);
    // A superseded request must stay quiet: the review renderer's generic
    // catch handler would otherwise clear the newer, already-rendered page.
    return new Promise((resolve, reject) => {
      request.then(response => {
        if (!controller.signal.aborted) resolve(response);
      }, error => {
        if (!controller.signal.aborted) reject(error);
      }).finally(() => {
        if (reviewRequests.get(productId) === controller) reviewRequests.delete(productId);
      });
    });
  };

  const settleReviewRoot = (root) => {
    const summary = root.querySelector('[data-review-summary-count]');
    const widget = root.querySelector('.rhp-product-reviews__widget');
    if (!summary || !widget) return;
    const reveal = () => {
      if (root.dataset.reviewReady !== 'true') root.dataset.reviewReady = 'true';
    };
    if (root.dataset.rhpSummarySettled === 'true' || root.dataset.rhpSummaryRequested === 'error') reveal();
    if (summary.textContent && !/^(Loading|Cargando)\b/i.test(summary.textContent.trim())) reveal();
    if (root.dataset.rhpPerformanceObserver === 'true') return;
    root.dataset.rhpPerformanceObserver = 'true';
    const observer = new MutationObserver(() => {
      if (root.dataset.rhpSummaryRequested === 'error' || (summary.textContent && !/^(Loading|Cargando)\b/i.test(summary.textContent.trim()))) {
        reveal();
        observer.disconnect();
      }
    });
    observer.observe(summary, { childList: true, characterData: true, subtree: true });
    observer.observe(root, { attributes: true, attributeFilter: ['data-rhp-summary-requested'] });
  };

  const observe = (scope = document) => {
    const roots = [];
    if (scope.matches?.(reviewSelector)) roots.push(scope);
    scope.querySelectorAll?.(reviewSelector).forEach(root => roots.push(root));
    roots.forEach(settleReviewRoot);
  };

  const init = () => observe(document);
  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init, { once: true })
    : init();
  document.addEventListener('shopify:section:load', event => observe(event.target));
})();
